// Skin quiz drawer (sections/skin-quiz.liquid).
// Kept out of theme.js on purpose: that file is a build artefact of sources that do not live in this repository.

const DRAWER_ID = 'skin-quiz-drawer';
const QUIZ_HASH = '#skin-quiz';
// Pause between picking an answer and the next question, long enough to see the choice selected
const AUTO_ADVANCE_MS = 350;

// ---------------------------------------------------------------------------------------------------------------------
// Klaviyo signup errors. A failed signup gets a reason, reported to Clarity (event + tag) and the console, so a broken
// list ID or blocked request shows up in Clarity by name instead of as one generic error message:
// - invalid_email: Klaviyo rejected the address (e.g. "name@gmail" without .com)
// - invalid_list:  the list or account ID is wrong (see snippets/klaviyo-ids.liquid)
// - blocked:       the request never reached Klaviyo (ad blocker, privacy setting, offline)
// - http_<status>: any other refusal
// Same helper in assets/scratch-popup.js and assets/skin-quiz.js.
// ---------------------------------------------------------------------------------------------------------------------
class SignupError extends Error {
  constructor(reason, detail = '') {
    super(`Klaviyo signup failed: ${reason}${detail ? ` (${detail})` : ''}`);
    this.reason = reason;
  }
}

const klaviyoSubscribe = async (url, options) => {
  let response;
  try {
    response = await fetch(url, options);
  } catch {
    throw new SignupError('blocked');
  }
  if (response.ok) return;

  const error = await response.json().then((json) => json.errors?.[0] || {}).catch(() => ({}));
  const where = `${error.source?.pointer || ''} ${error.detail || ''}`.toLowerCase();
  const reason = where.includes('email') ? 'invalid_email' : /list|group|company/.test(where) ? 'invalid_list' : `http_${response.status}`;
  throw new SignupError(reason, error.detail);
};

const reportSignupError = (source, error) => {
  const reason = error?.reason || 'unknown';
  console.warn(`[${source}]`, error?.message || error);
  try {
    window.clarity?.('event', `signup_error_${reason}`);
    window.clarity?.('set', 'signup_error', `${source}: ${reason} (${window.Shopify?.locale || ''})`);
  } catch { /* Clarity not loaded */ }
};

// Answers that change the outcome; the rest only feed Klaviyo and the result copy.
const SCENT_TO_PRODUCT = { original: 'original', citrus: 'citrus', rose: 'rose', coconut: 'coconut', lavender: 'lavender' };

const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`__${key}__`, value), template);

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

// The Liquid `t` filter HTML-escapes translations (it's -> it&#39;s); the strings are inserted as text, so decode them once.
const decodeEntities = (value) => {
  if (typeof value === 'string') return new DOMParser().parseFromString(`<!doctype html><body>${value}`, 'text/html').body.textContent;
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, decodeEntities(item)]));
  return value;
};

const formatMoney = (cents) => {
  const currency = window.Shopify?.currency?.active || 'EUR';
  return new Intl.NumberFormat(document.documentElement.lang || 'nl', { style: 'currency', currency }).format(cents / 100);
};

// ---------------------------------------------------------------------------------------------------------------------
// One welcome offer per visitor, shared with the scratch card (assets/scratch-popup.js keeps the same keys)
// ---------------------------------------------------------------------------------------------------------------------
// The scratch card draws the visitor's prize on their first visit. Whichever they fill in first, the scratch card or this
// quiz, claims that same prize: the code is stored as claimed, the newsletter / scratch popup stops showing, and the
// other one recognises the visitor instead of asking for their email again or handing out a second code.
const OFFER_CLAIM_KEY = 'cocovaye:offer-claimed';
const POPUP_FILLED_KEY = 'theme:popup-filled';

const readClaim = () => {
  try {
    const claim = JSON.parse(localStorage.getItem(OFFER_CLAIM_KEY));
    return claim && (claim.code || claim.amount != null) ? claim : null;
  } catch {
    return null;
  }
};

const saveClaim = (offer, email) => {
  try {
    localStorage.setItem(OFFER_CLAIM_KEY, JSON.stringify({ code: offer.code || null, amount: offer.amount, label: offer.label, displayLabel: offer.displayLabel }));
    localStorage.setItem(POPUP_FILLED_KEY, 'true');
  } catch { /* private mode: the offer still works for this page view */ }

  // Lets Klaviyo's onsite script recognise the visitor from now on, so later quiz and shop events land on their profile
  try { window.klaviyo?.identify?.({ email }); } catch { /* Klaviyo blocked or not loaded */ }
};

class SkinQuiz extends HTMLElement {
  #answers = {};
  #history = [];
  #advanceTimer = null;
  #email = null;
  #cartTotal = 0;
  #result = null;
  #listening = false;

  connectedCallback() {
    this.products = JSON.parse(this.dataset.products);
    this.strings = decodeEntities(JSON.parse(this.dataset.strings));
    this.bagIcon = this.dataset.bagIcon || '';

    // The drawer is re-attached every time it opens, which runs this again. Listening once keeps one click one add:
    // before, every opening stacked another click handler and the result's button added the product that many times.
    if (this.#listening) return;
    this.#listening = true;

    this.#fillOfferTexts();

    this.addEventListener('click', this.#onClick.bind(this));
    this.querySelector('[data-step="email"]')?.addEventListener('submit', this.#onEmailSubmit.bind(this));
  }

  // Order depends on earlier answers: very sensitive skin skips the scent question because it routes to fragrance-free anyway.
  get #steps() {
    const steps = [];
    if (this.dataset.showRoutine === 'true') steps.push('routine');
    steps.push('goal', 'sensitivity');
    if (this.#answers.sensitivity !== 'very') steps.push('scent');
    steps.push('for', 'lips');
    // A visitor who already left their email (scratch card or an earlier quiz) goes straight to the result with their code
    if (this.dataset.emailStep === 'true' && !this.#email && !readClaim()) steps.push('email');
    steps.push('result');
    return steps;
  }

  #onClick(event) {
    const target = event.target.closest('[data-start], [data-answer], [data-back], [data-next], [data-skip-email], [data-restart], [data-add], [data-lip-addon]');
    if (!target) return;

    if (target.matches('[data-start]')) return this.#goTo(this.#steps[0]);
    if (target.matches('[data-back]')) return this.#back();
    if (target.matches('[data-next]')) return this.#next();
    if (target.matches('[data-skip-email]')) return this.#goTo('result');
    if (target.matches('[data-restart]')) return this.#restart();
    if (target.matches('[data-lip-addon]')) return this.#updateShipping();
    if (target.matches('[data-add]')) return this.#addToCart(target);

    // Picking an answer moves on by itself after a short pause, so the visitor sees their choice selected first. The
    // "next" button stays for going forward again after stepping back to an answered question.
    const question = target.closest('[data-step]').dataset.step;
    this.#answers[question] = target.dataset.answer;
    target.parentElement.querySelectorAll('[data-answer]').forEach((option) => option.setAttribute('aria-checked', option === target));
    this.querySelector('[data-next]').disabled = false;

    clearTimeout(this.#advanceTimer);
    this.#advanceTimer = setTimeout(() => {
      // Only when the visitor is still on that question (not after "back" or closing the drawer in between)
      if (this.querySelector('[data-step]:not([hidden])')?.dataset.step === question) this.#next();
    }, AUTO_ADVANCE_MS);
  }

  #next() {
    const current = this.querySelector('[data-step]:not([hidden])');
    if (!current) return;

    const steps = this.#steps;
    this.#goTo(steps[steps.indexOf(current.dataset.step) + 1]);
  }

  #goTo(step, { remember = true } = {}) {
    const current = this.querySelector('[data-step]:not([hidden])');
    if (remember && current && current.dataset.step !== step) this.#history.push(current.dataset.step);

    this.querySelectorAll('[data-step]').forEach((element) => (element.hidden = element.dataset.step !== step));

    if (step === 'email') this.#fillOfferTexts();
    if (step === 'result') this.#renderResult();

    const steps = this.#steps;
    const questionCount = steps.filter((name) => name !== 'result').length;
    const index = steps.indexOf(step);
    const stepElement = this.querySelector(`[data-step="${step}"]`);

    this.querySelector('[data-progress]').hidden = step === 'intro';
    this.querySelector('[data-progress-fill]').style.width = `${step === 'result' ? 100 : (index / questionCount) * 100}%`;

    // Only the questions use the shared bar; the intro, email and result steps carry their own buttons.
    const isQuestion = Boolean(stepElement?.classList.contains('skin-quiz__question'));
    this.querySelector('[data-nav]').hidden = !isQuestion;
    if (isQuestion) this.querySelector('[data-next]').disabled = !this.#answers[step];

    this.closest('.drawer')?.shadowRoot?.querySelector('[part="body"]')?.scrollTo({ top: 0 });
    stepElement?.querySelector('button, input')?.focus({ preventScroll: true });
  }

  #back() {
    clearTimeout(this.#advanceTimer);
    const previous = this.#history.pop();
    if (previous) this.#goTo(previous, { remember: false });
  }

  #restart() {
    this.#answers = {};
    this.#history = [];
    this.querySelectorAll('[data-answer]').forEach((option) => option.setAttribute('aria-checked', 'false'));
    this.#goTo('intro', { remember: false });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // Routing
  // ---------------------------------------------------------------------------------------------------------------------

  #route() {
    const { goal, sensitivity, scent, for: forWhom, lips } = this.#answers;
    const product = (key) => (this.products[key]?.available ? this.products[key] : null);
    const fragranceFree = sensitivity === 'very' || scent === 'none';
    const scentKey = fragranceFree || !product(SCENT_TO_PRODUCT[scent]) ? 'natural' : SCENT_TO_PRODUCT[scent];
    const wantsAntiAging = goal === 'lines' && !fragranceFree && product('anti_aging');

    // The jar that matches the answers; lavender + fine lines upgrades to the anti-aging cream.
    const jarKey = wantsAntiAging && scentKey === 'lavender' ? 'anti_aging' : scentKey;

    // Family and gift route to the bundle, unless the visitor needs fragrance-free: the bundle contains scented creams.
    const bundleHero = (forWhom === 'family' || forWhom === 'gift') && !fragranceFree && product('bundle');
    const heroKey = bundleHero ? 'bundle' : jarKey;
    const extras = [];

    if (bundleHero) {
      extras.push({ key: jarKey, title: this.strings.singleTitle, text: fill(this.strings.singleText, { PRODUCT: this.products[jarKey].title }) });
    } else if (fragranceFree) {
      const title = { family: this.strings.duoTitleFamily, gift: this.strings.duoTitleGift }[forWhom] || this.strings.duoTitle;
      extras.push({ key: jarKey, quantity: 2, title, text: fill(this.strings.duoText, { PRODUCT: this.products[jarKey].title }) });
    } else if (product('bundle')) {
      const bundle = this.products.bundle;
      extras.push({ key: 'bundle', title: this.strings.bundleTitle, text: fill(this.strings.bundleText, { PRODUCT: bundle.title, PERCENT: this.#savings(bundle) }) });
    }

    if (wantsAntiAging && jarKey !== 'anti_aging') {
      const difference = formatMoney(this.products.anti_aging.price - this.products[scentKey].price);
      extras.push({ key: 'anti_aging', title: this.strings.antiAgingTitle, text: fill(this.strings.antiAgingText, { PRODUCT: this.products.anti_aging.title, DIFF: difference }) });
    }

    if (lips === 'daily' && product('routine_bundle')) {
      extras.push({ key: 'routine_bundle', title: this.strings.routineTitle, text: fill(this.strings.routineText, { PRODUCT: this.products.routine_bundle.title }) });
    }

    const showLipAddon = (lips === 'daily' || lips === 'sometimes') && Boolean(product('lip_balm'));

    return { heroKey, extras, showLipAddon, fragranceFree };
  }

  #savings(product) {
    return product.compareAtPrice > product.price ? `${Math.round((1 - product.price / product.compareAtPrice) * 100)}%` : '';
  }

  #reason({ heroKey, fragranceFree }) {
    const { goal, sensitivity, scent, for: forWhom } = this.#answers;
    const values = { GOAL: this.strings.goal[goal], SCENT: this.strings.scent[scent] || '', FOR: this.strings.for[forWhom] || '', PRODUCT: this.products[heroKey].title };

    let template = this.strings.reasonScent;
    if (heroKey === 'bundle') template = this.strings.reasonBundle;
    else if (sensitivity === 'very') template = this.strings.reasonSensitive;
    else if (fragranceFree) template = this.strings.reasonNoScent;

    return fill(template, values);
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // Result
  // ---------------------------------------------------------------------------------------------------------------------

  #renderResult() {
    this.#result = this.#route();
    const { heroKey, extras, showLipAddon } = this.#result;
    const hero = this.products[heroKey];

    this.querySelector('[data-reason]').textContent = this.#reason(this.#result);

    const blurb = [this.strings.blurb[heroKey], this.strings.tone[this.#answers.goal]].filter(Boolean).join(' ');
    const lipBalm = this.products.lip_balm;
    const savings = this.#savings(hero);

    this.querySelector('[data-hero]').innerHTML = `
      <div class="skin-quiz__hero">
        <div class="skin-quiz__hero-top">
          <a href="${escapeHtml(hero.url)}" class="skin-quiz__hero-media">${hero.image ? `<img src="${escapeHtml(hero.image)}" alt="${escapeHtml(hero.title)}" loading="lazy">` : ''}</a>
          <div class="skin-quiz__hero-info">
            <p class="skin-quiz__hero-title">${escapeHtml(hero.title)}</p>
            <p class="skin-quiz__hero-text">${escapeHtml(blurb)}</p>
            <div class="skin-quiz__hero-meta">
              <p class="skin-quiz__price">${this.#priceHtml(hero)}</p>
              ${savings ? `<span class="skin-quiz__badge skin-quiz__badge--sm">${escapeHtml(fill(this.strings.save, { PERCENT: savings }))}</span>` : ''}
            </div>
          </div>
        </div>
        ${showLipAddon ? `
          <label class="skin-quiz__addon">
            <input type="checkbox" data-lip-addon>
            <span class="skin-quiz__addon-media">${lipBalm.image ? `<img src="${escapeHtml(lipBalm.image)}" alt="" loading="lazy">` : ''}</span>
            <span class="skin-quiz__addon-info">
              <span class="skin-quiz__addon-title">${escapeHtml(fill(this.strings.lipAddon, { PRODUCT: lipBalm.title }))}</span>
              <span class="skin-quiz__addon-price">+${formatMoney(lipBalm.price)}</span>
            </span>
          </label>` : ''}
        <div class="skin-quiz__hero-actions">
          <button type="button" class="skin-quiz__cta skin-quiz__cta--split" data-add="${heroKey}" data-hero-add>
            <span>${escapeHtml(this.strings.addToCart)}</span>${this.bagIcon}
          </button>
          <a href="${escapeHtml(hero.url)}" class="skin-quiz__text-link">${escapeHtml(this.strings.viewProduct)}</a>
        </div>
      </div>`;

    this.querySelector('[data-extras]').innerHTML = extras.map((extra) => this.#extraHtml(extra)).join('');

    // The visitor's one code: from this quiz's signup or from the scratch card, never a second one
    const claim = readClaim();
    const codeElement = this.querySelector('[data-code]');
    codeElement.hidden = !claim;
    if (claim) {
      // With code delivery by email there is no code on the page: the visitor's unique code is in their inbox
      codeElement.innerHTML = fill(escapeHtml(claim.code ? this.strings.codeLine : this.strings.codeLineEmail), {
        CODE: `<strong>${escapeHtml(claim.code || '')}</strong>`,
        AMOUNT: escapeHtml(claim.displayLabel || claim.label || ''),
        MINIMUM: escapeHtml(this.dataset.discountMinimum || '')
      });
      if (claim.code) this.#applyCode(claim.code);
    }

    this.#track();
    this.#refreshCartTotal();
  }

  #priceHtml(product, quantity = 1) {
    const price = `<span>${formatMoney(product.price * quantity)}</span>`;
    return product.compareAtPrice > product.price ? `${price} <s>${formatMoney(product.compareAtPrice * quantity)}</s>` : price;
  }

  #extraHtml({ key, quantity = 1, title, text }) {
    const product = this.products[key];

    // The design leads with the offer line and puts the question underneath it, so text comes before title.
    return `
      <div class="skin-quiz__card" data-card="${key}">
        ${product.image ? `<a href="${escapeHtml(product.url)}" class="skin-quiz__card-media"><img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy"></a>` : ''}
        <div class="skin-quiz__card-info">
          <div class="skin-quiz__card-copy">
            <p class="skin-quiz__card-text">${escapeHtml(text)}</p>
            <p class="skin-quiz__card-title">${escapeHtml(title)}</p>
            <p class="skin-quiz__price skin-quiz__price--sm">${this.#priceHtml(product, quantity)}</p>
          </div>
          <button type="button" class="skin-quiz__cta skin-quiz__cta--sm" data-add="${key}" data-quantity="${quantity}">${escapeHtml(this.strings.add)}</button>
        </div>
      </div>`;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // Free shipping nudge
  // ---------------------------------------------------------------------------------------------------------------------

  async #refreshCartTotal() {
    try {
      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      this.#cartTotal = cart.total_price;
    } catch {
      this.#cartTotal = 0;
    }
    this.#updateShipping();
  }

  #updateShipping() {
    const element = this.querySelector('[data-shipping]');
    const threshold = Math.round(Number(this.dataset.shippingThreshold) * (Number(window.Shopify?.currency?.rate) || 1));
    if (!this.#result || !threshold) return (element.hidden = true);

    const addon = this.querySelector('[data-lip-addon]')?.checked ? this.products.lip_balm.price : 0;
    const total = this.#cartTotal + this.products[this.#result.heroKey].price + addon;
    const remaining = threshold - total;

    element.textContent = remaining > 0 ? fill(this.strings.shippingRemaining, { AMOUNT: formatMoney(remaining) }) : this.strings.shippingReached;
    element.classList.toggle('skin-quiz__note--reached', remaining <= 0);
    element.hidden = false;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // Cart
  // ---------------------------------------------------------------------------------------------------------------------

  async #addToCart(button) {
    // A second click while the first request is still running would add the product again
    if (button.hasAttribute('aria-busy')) return;

    const key = button.dataset.add;
    const items = [{ id: this.products[key].variantId, quantity: Number(button.dataset.quantity || 1), properties: { _source: 'Skin quiz' } }];

    if (button.hasAttribute('data-hero-add') && this.querySelector('[data-lip-addon]')?.checked) {
      items.push({ id: this.products.lip_balm.variantId, quantity: 1, properties: { _source: 'Skin quiz' } });
    }

    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname })
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || json.message);

      window.klaviyo?.push?.(['track', 'Skin Quiz Add To Cart', { product: this.products[key].title, ...this.#properties() }]);

      if (window.themeVariables?.settings?.cartType === 'page') {
        window.location.href = `${window.Shopify.routes.root}cart`;
        return;
      }

      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      cart.sections = json.sections;

      await document.getElementById(DRAWER_ID)?.hide?.();
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    } catch (error) {
      document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: error.message || this.strings.addError, tone: 'error' } }));
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // Klaviyo
  // ---------------------------------------------------------------------------------------------------------------------

  #properties() {
    const properties = Object.fromEntries(Object.entries(this.#answers).map(([question, answer]) => [`skin_quiz_${question}`, answer]));
    if (this.#result) properties.skin_quiz_match = this.products[this.#result.heroKey].title;
    return properties;
  }

  // Only reaches Klaviyo for visitors it already knows (earlier signup, email click); anonymous visitors are ignored.
  #track() {
    window.klaviyo?.push?.(['track', 'Completed Skin Quiz', this.#properties()]);
  }

  async #onEmailSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const input = form.querySelector('input[type="email"]');
    const button = form.querySelector('[type="submit"]');
    const errorElement = form.querySelector('[data-email-error]');
    const email = input.value.trim();

    if (!input.checkValidity() || !email) {
      errorElement.textContent = this.strings.emailError;
      errorElement.hidden = false;
      return;
    }

    errorElement.hidden = true;
    button.setAttribute('aria-busy', 'true');

    try {
      // The match is computed before the call so it lands on the profile together with the answers.
      this.#result = this.#route();
      const offer = this.#offer();
      await this.#subscribe(email, offer);
      this.#email = email;
      if (offer) saveClaim(offer, email);

      this.#goTo('result');
    } catch (error) {
      reportSignupError('Skin quiz', error);
      errorElement.textContent = this.strings.emailError;
      errorElement.hidden = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  // The offer this visitor gets: their scratch card prize when the scratch card is on the page (not for logged in
  // customers), otherwise the code set on this section. Null when no code is set at all.
  // With code delivery by email (Theme settings > Welcome offer) the offer is only an amount: Klaviyo mails a unique code.
  #offer() {
    const byEmail = this.dataset.delivery === 'email';
    const prize = document.querySelector('scratch-card')?.prize;
    if (prize && (prize.code || byEmail)) return { code: prize.code || null, amount: prize.amount, label: prize.label, displayLabel: prize.displayLabel || prize.label };

    const amount = Number(this.dataset.discountAmount) || null;
    if (byEmail) return amount ? { code: null, amount, label: this.dataset.discountLabel, displayLabel: this.dataset.discountLabel } : null;
    if (!this.dataset.discountCode) return null;
    return { code: this.dataset.discountCode, amount, label: this.dataset.discountLabel, displayLabel: this.dataset.discountLabel };
  }

  // The email step's texts carry __AMOUNT__ and __MINIMUM__, filled with the visitor's own offer
  #fillOfferTexts() {
    const offer = this.#offer();
    this.querySelectorAll('[data-offer-template]').forEach((element) => {
      element.textContent = fill(element.dataset.offerTemplate, { AMOUNT: offer?.displayLabel || '', MINIMUM: this.dataset.discountMinimum || '' });
    });
  }

  // Visiting /discount/<code> stores the code in the session, so the cart and checkout pick it up. Once per page view.
  #applyCode(code) {
    if (this.appliedCode === code) return;
    this.appliedCode = code;
    fetch(`${window.Shopify.routes.root}discount/${encodeURIComponent(code)}`).catch(() => {});
  }

  async #subscribe(email, offer) {
    // Same profile properties as the scratch card, so the welcome flow mails the code whichever way the visitor came in
    const offerProperties = offer ? { scratch_prize: offer.amount, scratch_prize_label: offer.label, ...(offer.code ? { scratch_code: offer.code, skin_quiz_code: offer.code } : {}) } : {};

    await klaviyoSubscribe(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/vnd.api+json', revision: '2026-07-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Skin quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: { email, properties: { ...this.#properties(), ...offerProperties } }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: this.dataset.listId } } }
        }
      })
    });

  }
}

if (!window.customElements.get('skin-quiz')) {
  window.customElements.define('skin-quiz', SkinQuiz);
}

// ---------------------------------------------------------------------------------------------------------------------
// Triggers: header button uses aria-controls (handled by the theme dialog). Plain links to #skin-quiz (menus, ads, emails)
// are handled here, as is landing on a URL with #skin-quiz.
// ---------------------------------------------------------------------------------------------------------------------

const openQuiz = () => document.getElementById(DRAWER_ID)?.show?.();

document.addEventListener('click', (event) => {
  const link = event.target.closest(`a[href$="${QUIZ_HASH}"]`);
  if (!link || link.getAttribute('aria-controls') === DRAWER_ID) return;

  event.preventDefault();
  document.querySelectorAll('#menu-drawer[open], .drawer[open]').forEach((drawer) => drawer.hide?.());
  openQuiz();
});

if (window.location.hash === QUIZ_HASH) {
  window.customElements.whenDefined('x-drawer').then(openQuiz);
}
